import { Box } from '@mantine/core';
import type { NotificationRuleOut } from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { Text, TextSkeleton, Title } from '@repo/foundations';
import { NotificationRuleCard } from './NotificationRuleCard';

interface NotificationRulesListProps {
	rules: NotificationRuleOut[];
	isLoading: boolean;
	onCreateRule: () => Promise<NotificationRuleOut | undefined>;
	onEditRule: (rule: NotificationRuleOut) => void;
	onDeleteRule: (ruleId: string) => Promise<void>;
}

export function NotificationRulesList({
	rules,
	isLoading,
	onCreateRule,
	onEditRule,
	onDeleteRule,
}: NotificationRulesListProps) {
	const handleCreateRule = async () => {
		const newRule = await onCreateRule();
		if (newRule) {
			onEditRule(newRule);
		}
	};

	const handleEditRule = (rule: NotificationRuleOut) => {
		onEditRule(rule);
	};

	const handleDeleteRule = async (id: string) => {
		await onDeleteRule(id);
	};

	const ruleCount = rules.length;

	return (
		<Box>
			<Title size="md" mb="md">
				Notification Rules
			</Title>
			<Text size="sm" color="text/secondary/default" mb="md">
				Configure notification rules for monitoring alerts
			</Text>

			<Box mb="4xl">
				<SettingsButton
					iconName="plus"
					variant="tertiary"
					title={
						isLoading ? (
							<TextSkeleton width={120} />
						) : (
							`${ruleCount} ${ruleCount === 1 ? 'notification rule' : 'notification rules'}`
						)
					}
					onClick={handleCreateRule}
					aria-label="Create notification rule"
					tooltip="Create notification rule"
				/>

				{rules.map((rule) => (
					<NotificationRuleCard
						key={rule.id}
						rule={rule}
						onEdit={handleEditRule}
						onDelete={handleDeleteRule}
					/>
				))}
			</Box>
		</Box>
	);
}
